import type { PropFirm, PropFirmPlan, PropFirmAccount } from '../../types';
import { ICE, TEAL, ACCENT, FIELD, PX_FONT } from '../guild/theme';

/* One-glance summary of a prop firm's rules for the chosen plan and account size: evaluation,
   funded stage, payouts and firm limits, so traders don't have to dig through the firm's help pages. */

const usd = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n === 0 ? 'Free' : `$${n.toLocaleString()}`);
const pct = (n: number | null | undefined) => (n === null || n === undefined ? 'None' : `${Math.round(n * 100)}%`);
const DD: Record<string, string> = { trailing: 'trailing (intraday)', eod: 'end-of-day trailing', static: 'static' };
const label = { fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.12em', color: TEAL } as const;

function Group({ title, rows }: { title: string; rows: [string, string][] }) {
  return (
    <div style={{ background: FIELD, borderRadius: 'var(--pal-radius, 0)', padding: 14, boxShadow: `inset 3px 0 0 ${ACCENT}` }}>
      <div style={{ ...label, color: ACCENT, fontWeight: 800, marginBottom: 10 }}>{title}</div>
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 14, rowGap: 7 }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}>
            <dt style={{ ...label, letterSpacing: '0.06em' }}>{k}</dt>
            <dd style={{ margin: 0, fontFamily: PX_FONT, fontSize: 13, fontWeight: 600, color: ICE, textAlign: 'right' }}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** The rules grouped for display: evaluation, funded stage, payouts and firm limits */
export function ruleGroups(firm: PropFirm, plan: PropFirmPlan, account: PropFirmAccount) {
  const fundedDD = account.fundedMaxDrawdown ?? account.maxDrawdown;
  const fundedType = plan.fundedDrawdownType ?? plan.drawdownType;
  const groups: { title: string; rows: [string, string][] }[] = [
    ...(plan.hasEval ? [{
      title: 'Evaluation',
      rows: [
        ['Profit target', usd(account.profitTarget)],
        ['Max drawdown', `${usd(account.maxDrawdown)} · ${DD[plan.drawdownType] ?? plan.drawdownType}`],
        ['Daily loss limit', account.dailyLossLimit === null ? 'None' : usd(account.dailyLossLimit)],
        ['Max contracts', account.maxContracts ? String(account.maxContracts) : '—'],
        ['Consistency rule', pct(plan.consistencyRulePct)],
        ['Time limit', plan.evalDays ? `${plan.evalDays} days` : 'None'],
      ] as [string, string][],
    }] : []),
    {
      title: plan.hasEval ? 'Funded account' : 'Funded (instant, no evaluation)',
      rows: [
        ['Max drawdown', `${usd(fundedDD)} · ${DD[fundedType] ?? fundedType}`],
        ['Daily loss limit', (account.fundedDailyLossLimit ?? account.dailyLossLimit) === null ? 'None' : usd(account.fundedDailyLossLimit ?? account.dailyLossLimit)],
        ['Max contracts', account.fundedMaxContracts ? String(account.fundedMaxContracts) : account.maxContracts ? String(account.maxContracts) : '—'],
        ['Consistency rule', pct(plan.fundedConsistencyRulePct)],
        ['Activation fee', usd(account.activationFee)],
      ],
    },
    {
      title: 'Payouts & limits',
      rows: [
        ['Profit split', pct(plan.payoutSplit)],
        ['Payout frequency', plan.payoutFrequency],
        ['Max evaluation accounts', firm.maxEvalAccounts === null ? 'Unlimited' : String(firm.maxEvalAccounts)],
        ['Max funded accounts', firm.maxFundedAccounts === null ? 'Unlimited' : String(firm.maxFundedAccounts)],
      ],
    },
  ];
  return groups;
}

export function RulesSnapshot({ firm, plan, account, sources, verifiedOn }: {
  firm: PropFirm; plan: PropFirmPlan; account: PropFirmAccount; sources: string[]; verifiedOn: string | null;
}) {
  const groups = ruleGroups(firm, plan, account);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, animation: 'pal-swap-in 0.3s ease-out both' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {groups.map(g => <Group key={g.title} title={g.title} rows={g.rows} />)}
      </div>
      <div style={{ ...label, lineHeight: 1.6 }}>
        {firm.name} · {plan.name} · ${account.size.toLocaleString()}
        {verifiedOn ? ` · verified ${new Date(verifiedOn).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
        {' · Always confirm with the firm before you buy.'}
        {sources.length > 0 && <> Sources: {sources.map((s, i) => <a key={s} href={s} target="_blank" rel="noreferrer" style={{ color: ACCENT }}>{i ? ', ' : ''}{new URL(s).hostname.replace(/^www\./, '')}</a>)}</>}
      </div>
    </div>
  );
}
